import { useQuery } from "@tanstack/react-query"
import React, { createContext, useContext, useState } from "react"

import { useAppContext } from "@contexts/app_context"
import {
  CAMPAIGN_LINKS_QUERY_KEYS,
  resolveCampaignTrackingLink
} from "@domain/campaign_links"
import { type CampaignTrackingLink } from "@models/campaign_links"

type CampaignLinksPageContextType = {
  translate: (key: string) => string
  search: (url: string) => void
  submittedUrl: string
  result: CampaignTrackingLink | null
  isLoading: boolean
}

const CampaignLinksPageContext = createContext<CampaignLinksPageContextType>({
  translate: (key: string) => key,
  search: () => undefined,
  submittedUrl: "",
  result: null,
  isLoading: false
})

export function CampaignLinksPageContextProvider(props: {
  children: JSX.Element | JSX.Element[]
}) {
  const { translate } = useAppContext()
  const [submittedUrl, setSubmittedUrl] = useState("")

  const { data, isFetching } = useQuery({
    queryKey: [CAMPAIGN_LINKS_QUERY_KEYS.lookup, submittedUrl],
    queryFn: async () =>
      (await resolveCampaignTrackingLink(submittedUrl)).data ?? null,
    enabled: !!submittedUrl
  })

  return (
    <CampaignLinksPageContext.Provider
      value={{
        translate,
        search: (url: string) => setSubmittedUrl(url.trim()),
        submittedUrl,
        result: data ?? null,
        isLoading: isFetching
      }}
    >
      {props.children}
    </CampaignLinksPageContext.Provider>
  )
}

export function useCampaignLinksPageContext() {
  return useContext(CampaignLinksPageContext)
}
